import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

/**
 * Password field component that integrates with React Hook Form
 * Includes toggle to show/hide password
 */
interface FormPasswordFieldProps {
  name: string;
  label: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
}

export const FormPasswordField: React.FC<FormPasswordFieldProps> = ({
  name,
  label,
  helperText,
  required = false,
  disabled = false,
}) => {
  const { control, formState: { errors } } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  const errorMessage = errors[name]?.message as string | undefined;

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl 
          variant="outlined" 
          fullWidth 
          error={!!errorMessage}
          disabled={disabled}
          required={required}
          margin="normal"
        >
          <InputLabel htmlFor={`password-field-${name}`}>{label}</InputLabel>
          <OutlinedInput
            id={`password-field-${name}`}
            type={showPassword ? 'text' : 'password'}
            {...field}
            value={field.value ?? ''}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label={label}
          />
          {(errorMessage || helperText) && (
            <FormHelperText error={!!errorMessage}>
              {errorMessage || helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default FormPasswordField;